import React from 'react';
import { MenuItem, TextField } from '@mui/material';
import { useQuery } from 'react-query';
import api from '../../utils/api';

export interface StockLocationOption {
  id: string;
  name: string;
  type: 'WAREHOUSE' | 'TRUCK' | 'SITE' | string;
  isDefault?: boolean;
  isActive?: boolean;
}

interface DeductFromLocationSelectProps {
  /** '' = Automatic (send nothing — the server infers per ADR-0013). */
  value: string;
  onChange: (locationId: string) => void;
  disabled?: boolean;
  size?: 'small' | 'medium';
  /** margin passthrough so the control sits naturally in dense dialogs */
  margin?: 'none' | 'dense' | 'normal';
}

/**
 * "Deduct stock from" — the visible-but-frictionless override for the
 * truck-dispense inference (ADR-0013).
 *
 * Design (validated against HVMS's per-line "From:" and ezyVet Go's 2023
 * retrofit): the DEFAULT option is "Automatic", which sends NOTHING — the
 * server resolves explicit > acting vet's truck > warehouse. Only an explicit
 * user choice sends a dispenseLocationId. That keeps the control offline-safe
 * (no fetch needed for the default path) and makes FE/BE inference drift
 * impossible.
 *
 * Renders nothing when the clinic has no active TRUCK location — a
 * single-location clinic never sees stock-location noise on clinical dialogs.
 * Renders nothing while locations are unknown (loading/offline/error): the
 * dispense then simply omits the field and the server infers — graceful, not
 * blocking.
 */
const DeductFromLocationSelect: React.FC<DeductFromLocationSelectProps> = ({
  value,
  onChange,
  disabled,
  size = 'small',
  margin = 'dense',
}) => {
  const { data: locations } = useQuery<StockLocationOption[]>(
    ['inventory-locations'],
    async () => {
      const response = await api.get('/inventory/locations');
      return response.data;
    },
    { staleTime: 5 * 60 * 1000 },
  );

  const active = (locations ?? []).filter((loc) => loc.isActive !== false);
  const hasTruck = active.some((loc) => loc.type === 'TRUCK');
  if (!hasTruck) {
    return null;
  }

  const typeLabel = (type: string) =>
    type === 'TRUCK' ? 'Truck' : type === 'SITE' ? 'Site' : 'Warehouse';

  return (
    <TextField
      select
      label="Deduct stock from"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      size={size}
      margin={margin}
      fullWidth
      helperText={
        value === ''
          ? 'Automatic: your truck if one is assigned to you, otherwise the main warehouse'
          : undefined
      }
    >
      <MenuItem value="">Automatic</MenuItem>
      {active.map((loc) => (
        <MenuItem key={loc.id} value={loc.id}>
          {loc.name} · {typeLabel(loc.type)}
        </MenuItem>
      ))}
    </TextField>
  );
};

export default DeductFromLocationSelect;
